import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, Mail } from 'lucide-react';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store auth token
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminEmail', email);
        
        toast.success('Login successful!', {
          description: 'Redirecting to dashboard...',
        });

        // Redirect to Payment Plans (other sections need the unlock password)
        setTimeout(() => navigate('/admin/payment-plans'), 500);
      } else {
        toast.error('Login failed', {
          description: data.detail || 'Invalid email or password',
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed', {
        description: 'Unable to connect to server',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Login | Navigating Minds Psychology</title>
        {/* Prevent search engines from indexing admin pages */}
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F5F1E8] via-[#FDFBF7] to-[#E8E4DC] py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
            {/* Header */}
            <div className="text-center">
              <div className="mx-auto h-16 w-16 bg-[#8B9D83]/10 rounded-full flex items-center justify-center mb-4">
                <Lock className="h-8 w-8 text-[#8B9D83]" />
              </div>
              <h2 className="text-3xl font-semibold text-[#3A3530] font-heading">
                Admin Portal
              </h2>
              <p className="mt-2 text-[#6B6560]">
                Sign in to access the assessment dashboard
              </p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#3A3530] font-medium">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#9B9590]" />
                  <Input
                    id="email"
                    data-testid="admin-login-email-input"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 border-[#E8E4DC] focus:border-[#8B9D83] focus:ring-2 focus:ring-[#8B9D83]/20 rounded-lg"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#3A3530] font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#9B9590]" />
                  <Input
                    id="password"
                    data-testid="admin-login-password-input"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 border-[#E8E4DC] focus:border-[#8B9D83] focus:ring-2 focus:ring-[#8B9D83]/20 rounded-lg"
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="admin-login-submit-button"
                disabled={isLoading}
                className="w-full bg-[#8B9D83] hover:bg-[#6B7D63] text-white font-medium py-3 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            {/* Footer */}
            <div className="text-center text-sm text-[#9B9590]">
              <p>Authorized access only</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
